import React, {forwardRef, Fragment, useCallback, useState} from "react";
import {BottomNavigation, BottomNavigationAction} from "@mui/material";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import MetadataIcon from "../custom-icons/MetadataIcon";
import FullscreenDialog from "../fullscreen-dialog";
import PackageEditButton from "../package-manager/PackageEditButton";
import PackageMetadataViewer from "../package-metadata-viewer";
import {appConfigMetadataViewModeSelector} from "../../state/app-config/appConfigSelectors";

export const METADATA_BUTTON_VIEW_MODE_BOTTOM_NAVIGATION_BUTTON = "METADATA_BUTTON_VIEW_MODE_BOTTOM_NAVIGATION_BUTTON";
export const METADATA_BUTTON_VIEW_MODE_ICON_BUTTON = "METADATA_BUTTON_VIEW_MODE_ICON_BUTTON";
export const METADATA_BUTTON_VIEW_MODE_BUTTON = "METADATA_BUTTON_VIEW_MODE_BUTTON";
export const METADATA_BUTTON_VIEW_MODE_HIDDEN = "METADATA_BUTTON_VIEW_MODE_HIDDEN";

const PackageMetadataButton = forwardRef(
  (
    {
      nodeId,
      nodeType,
      packageId,
      metadataUrl,
      sx,
      label,
      iconSize,
      viewMode = METADATA_BUTTON_VIEW_MODE_BOTTOM_NAVIGATION_BUTTON,
      buttonProps = {},
      metadataType,
      catalog
    },
    ref
  ) => {
    const {t} = useTranslation();

    const metadataViewMode = useSelector(appConfigMetadataViewModeSelector);

    const activeNodeId = useSelector(state => state.node?.nodeId);

    const resolvedNodeId = nodeId || activeNodeId;

    const resolvedNodeType = useSelector(state => {
      const targetNodeId = nodeId || state.node?.nodeId;
      if (!targetNodeId) return nodeType || null;

      // 1. Check if it's the current active node
      if (state.node?.nodeId === targetNodeId) {
        return state.node.type || state.node.nodeType || nodeType || null;
      }

      // 2. Otherwise search among the federated nodes of the Hub
      const federatedNode = state.hub?.nodes?.find(n => n.nodeId === targetNodeId);
      return federatedNode?.type || federatedNode?.nodeType || nodeType || null;
    });

    const [isVisible, setVisibility] = useState(false);

    const onOpen = useCallback(
      event => {
        event.stopPropagation();
        if (metadataViewMode === "new_tab") {
          window.open(metadataUrl, "_blank");
        } else if (metadataViewMode === "popup") {
          window.open(metadataUrl, "_blank", "popup, left=100, top=100, width=1024, height=720");
        } else {
          setVisibility(true);
        }
      },
      [metadataViewMode, metadataUrl]
    );

    const onClose = useCallback(() => {
      setVisibility(false);
    }, []);

    return (
      <Fragment>
        {viewMode === METADATA_BUTTON_VIEW_MODE_BOTTOM_NAVIGATION_BUTTON ? (
          <BottomNavigation showLabels onChange={onOpen} style={{marginBottom: 4}}>
            <BottomNavigationAction
              id="dataset-metadata-btn"
              className="dataset-metadata-btn"
              label={label || t("components.datasetMetadataButton.label")}
              icon={<MetadataIcon />}
              role="menuitem"
            />
          </BottomNavigation>
        ) : viewMode === METADATA_BUTTON_VIEW_MODE_ICON_BUTTON ? (
          <Tooltip title={label || t("components.datasetMetadataButton.label")}>
            <IconButton
              id="dataset-metadata-btn"
              className="dataset-metadata-btn"
              size={iconSize}
              onClick={onOpen}
              aria-label={label || t("components.datasetMetadataButton.label")}
              sx={sx}
            >
              <MetadataIcon />
            </IconButton>
          </Tooltip>
        ) : viewMode === METADATA_BUTTON_VIEW_MODE_BUTTON ? (
          <Tooltip title={label || t("components.datasetMetadataButton.label")}>
            <Button
              onClick={onOpen}
              id="dataset-metadata-btn"
              className="dataset-metadata-btn"
              color="secondary"
              variant="contained"
              style={{whiteSpace: "nowrap"}}
              sx={sx}
              {...buttonProps}
            >
              {label || t("components.datasetMetadataButton.label")}
            </Button>
          </Tooltip>
        ) : viewMode === METADATA_BUTTON_VIEW_MODE_HIDDEN ? (
          <Button
            ref={ref}
            id="dataset-metadata-btn"
            className="dataset-metadata-btn"
            sx={{display: "none"}}
            onClick={onOpen}
          />
        ) : null}

        <FullscreenDialog
          open={isVisible}
          onClose={onClose}
          className="package-metadata-button__dialog"
          title={label || t("components.datasetMetadataButton.label")}
        >
          <DialogContent className="package-metadata-button__dialog__content">
            <PackageMetadataViewer
              nodeId={resolvedNodeId}
              nodeType={resolvedNodeType}
              catalog={catalog}
              packageId={packageId}
              metadataUrl={metadataUrl}
              metadataType={metadataType}
            />
          </DialogContent>
          <DialogActions className="package-metadata-button__dialog__actions">
            <PackageEditButton
              nodeId={resolvedNodeId}
              nodeType={resolvedNodeType}
              packageId={packageId}
              callback={onClose}
            />
            <Button onClick={onClose}>{t("commons.confirm.close")}</Button>
          </DialogActions>
        </FullscreenDialog>
      </Fragment>
    );
  }
);

export default PackageMetadataButton;
